import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/i18n/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";

import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Analyze from "@/pages/Analyze";
import AnalyzePremium from "@/pages/AnalyzePremium";
import Premium from "@/pages/Premium";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminOnly from "@/components/AdminOnly";
import BillingSuccess from "@/pages/BillingSuccess";
import Report from "@/pages/Report";
import History from "@/pages/History";
import SharedReport from "@/pages/SharedReport";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";

// Handles the Emergent Google OAuth return: {redirect}#session_id=xxx
function GoogleCallback() {
  const { googleExchange } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sessionId) { navigate("/login", { replace: true }); return; }
      try {
        await googleExchange(sessionId);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("Zalogowano przez Google");
        navigate("/dashboard", { replace: true });
      } catch {
        toast.error("Logowanie Google nie powiodło się");
        navigate("/login", { replace: true });
      }
    })();
  }, [googleExchange, navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#ff4458]" />
    </div>
  );
}


function GoogleTokenCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      toast.error("Logowanie Google nie powiodło się");
      navigate("/login", { replace: true });
      return;
    }

    localStorage.setItem("dc_token", token);
    toast.success("Zalogowano przez Google");
    window.location.href = "/dashboard";
  }, [navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#ff4458]" />
    </div>
  );
}


function Shell() {
  const loc = useLocation();
  const isLanding = loc.pathname === "/";

  // Detect OAuth callback synchronously during render (before other routes).
  if (loc.hash?.includes("session_id=")) {
    return (
      <div className="flex min-h-screen flex-col bg-[var(--dc-bg)] text-[var(--dc-text)]">
        <GoogleCallback />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--dc-bg)] text-[var(--dc-text)]">
      <TopBar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Landing />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/google-callback" element={<GoogleTokenCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public utility routes */}
          <Route path="/share/:token" element={<SharedReport />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/cookies" element={<Cookies />} />

          {/* App */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/analyze" element={<Analyze />} />
          <Route path="/analyze/premium" element={<AnalyzePremium />} />
          <Route path="/premium" element={<Premium />} />
          <Route
            path="/admin/marketing"
            element={
              <AdminOnly>
                <AdminMarketing />
              </AdminOnly>
            }
          />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/report/:id" element={<Report />} />
          <Route path="/history" element={<History />} />
        </Routes>
      </main>
      {!isLanding && <Footer />}
      <CookieBanner />
      <Toaster theme="dark" position="top-center"
        toastOptions={{ style: { background: "#181b24", border: "1px solid #222636", color: "#fff" } }} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LangProvider>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </LangProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
